import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Crown } from 'lucide-react';
import { supabase } from '../supabaseClient';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { showErrorToast, showSuccessToast } from '@/lib/toast';
import { validatePassword } from '@/lib/passwordPolicy';

// Story 073 — Set a new password after clicking a recovery link.
// AuthConfirmPage has already verifyOtp'd the recovery token by the
// time we land here, so the session is live and we just need to call
// updateUser({ password }). Auth-required: the route is mounted
// behind the auth guard in main.tsx.
//
// Post-update we explicitly sign out and send the user to /login.
// Reasoning: the recovery-derived session is a "you proved access to
// the email" session, not a "you proved you know the password"
// session. Leaving it active means a shared-device next-visitor
// inherits a logged-in account. Forcing a re-login closes that loop
// and matches the convention most password-reset flows follow.

export function ResetPasswordPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showMismatch, setShowMismatch] = useState(false);
  const [showTooShort, setShowTooShort] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setShowMismatch(false);
    setShowTooShort(false);

    if (!validatePassword(password)) {
      setShowTooShort(true);
      return;
    }
    if (password !== confirm) {
      setShowMismatch(true);
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        // Story 075 — surface the raw Supabase error to the console
        // so we can debug failures (session expired, server-side
        // password policy, etc.) without changing the user-facing
        // toast.
        console.error('Password reset failed', error);
        const code = (error as { code?: string }).code;
        const isWeakPassword =
          code === 'weak_password' ||
          error.name === 'AuthWeakPasswordError' ||
          /known to be weak/i.test(error.message);
        const sameAsCurrent =
          code === 'same_password' ||
          /different from the old password/i.test(error.message);
        if (isWeakPassword) {
          showErrorToast(t('reset_password.errors.weak_password_breached'));
        } else if (sameAsCurrent) {
          showErrorToast(t('reset_password.errors.same_as_current'));
        } else {
          showErrorToast(t('reset_password.errors.unexpected'));
        }
        return;
      }
      // Sign out the recovery session before showing the toast +
      // redirecting; otherwise the user lands on /login but is still
      // technically signed in.
      await supabase.auth.signOut();
      showSuccessToast(t('reset_password.success_toast'));
      navigate('/login', { replace: true });
    } catch (err) {
      console.error('Password reset threw', err);
      showErrorToast(t('reset_password.errors.unexpected'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      data-testid="reset-password-page"
      className="flex min-h-screen items-center justify-center bg-background px-4 py-12"
    >
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-accent/20">
            <Crown className="h-6 w-6 text-accent-foreground" />
          </div>
          <CardTitle className="text-2xl">{t('reset_password.title')}</CardTitle>
          <CardDescription>{t('reset_password.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="new-password">
                {t('reset_password.new_password_label')}
              </Label>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                placeholder={t('reset_password.new_password_placeholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="new-password-input"
              />
              {showTooShort && (
                <p
                  data-testid="password-too-short-error"
                  className="text-sm text-destructive"
                >
                  {t('reset_password.errors.too_short')}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="confirm-password">
                {t('reset_password.confirm_password_label')}
              </Label>
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                placeholder={t('reset_password.confirm_password_placeholder')}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="confirm-password-input"
              />
              {showMismatch && (
                <p
                  data-testid="password-mismatch-error"
                  className="text-sm text-destructive"
                >
                  {t('reset_password.errors.mismatch')}
                </p>
              )}
            </div>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="reset-password-submit-button"
              className="w-full"
            >
              {submitting
                ? t('reset_password.submitting')
                : t('reset_password.submit')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
